'use client'

import * as React from 'react'
import { useSessionStorage } from '@uidotdev/usehooks'
import { cn } from '../../../lib/utils'
import { Input } from '../Input'
import { Icon } from '../Icon'
import { Avatar, AvatarImage, AvatarFallback } from '../Avatar'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../Collapsible'
import {
  ThreadStatusDotRail,
  getThreadStatusDotLineClass,
} from './ThreadStatusDot'

import { InfiniteScroll } from '../InfiniteScroll'
import {
  classifyThreadFilter,
  isCurrentMemberThreadParticipant,
  isThreadInConversations,
  isThreadInInbox,
  isFeedThread,
  getFeedRoutingLabel,
  threadMatchesSearch,
  type ThreadFilterType,
  type ThreadFilterable,
  type ThreadSearchable,
} from './threadFilters'
import { groupThreadsByContact } from './contactGrouping'
import type { ContactSortMode } from './types'
import {
  DropdownMenuComponent as DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../DropDownMenu'

export type { ContactSortMode } from './types'

export {
  classifyThreadFilter,
  isCurrentMemberThreadParticipant,
  isThreadInConversations,
  isThreadInInbox,
  isFeedThread,
  getFeedRoutingLabel,
  threadMatchesSearch,
  type ThreadFilterType,
  type ThreadFilterable,
  type ThreadSearchable,
} from './threadFilters'

export type ThreadParticipant = {
  id: string
  displayName: string
  avatarUrl: string | null
  kind: 'CONTACT' | 'MEMBER' | 'AGENT' | 'SYSTEM' | 'WORKFLOW' | 'BATCH_JOB'
  imageSeed?: string | null
  contactId?: string | null
  memberId?: string | null
  contactActivity?: {
    lastContactedAt: string | null
    lastActivityAt: string | null
    lastMessage: string | null
  } | null
}

export type ContactGroup = {
  contactId: string
  displayName: string
  avatarUrl: string | null
  imageSeed: string | null
  lastMessageAt: string | null
  lastMessagePreview: string | null
  lastContactedAt: string | null
  lastActivityAt: string | null
  displayTimeAt: string | null
  totalUnreadCount: number
  threads: ThreadItem[]
  nextScheduledAt: string | null
}

export type ThreadItem = {
  id: string
  title: string | null
  lastMessageAt: string | null
  lastMessagePreview: string | null
  lastActivityAt?: string | null
  unreadCount: number
  participants: ThreadParticipant[]
  routingParticipantId?: string | null
  communicationChannelId?: string | null
  hasAttention?: boolean
  routingPattern?: string | null
  routingPatternDisplay?: string | null
  routingAddress?: string | null
  isExclusiveFeed?: boolean
  // Status indicator fields
  hasActiveWorkflow?: boolean
  hasActiveAgent?: boolean
  pendingApprovalCount?: number
  hasScheduledMessage?: boolean
  nextScheduledAt?: string | null
  pinnedAt?: string | null
  kind?: 'CONVERSATION' | 'INTERNAL' | 'ESCALATION' | 'BROADCAST' | 'TESTING'
}

export type CommunicationChannel = {
  id: string
  name: string
  type: string
  icon?: string
  appIconUrl?: string
}

export type LinkComponentProps = {
  href: string
  className?: string
  children: React.ReactNode
  onClick?: (e: React.MouseEvent) => void
}

type ThreadNavLinkProps = {
  href?: string
  LinkComponent?: React.ComponentType<LinkComponentProps>
  onClick: () => void
  className: string
  children: React.ReactNode
}

/** Prefer Link when available so navigation supports right-click open in new tab. */
function ThreadNavLink({
  href,
  LinkComponent,
  onClick,
  className,
  children,
}: ThreadNavLinkProps) {
  if (href && LinkComponent) {
    return (
      <LinkComponent href={href} className={className} onClick={onClick}>
        {children}
      </LinkComponent>
    )
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  )
}

export type ThreadsSidebarProps = {
  threads: ThreadItem[]
  /** Exclusive feeds pinned above pagination; omitted while searching */
  exclusiveFeedThreads?: ThreadItem[]
  selectedThreadId: string | null
  currentMemberId: string | null
  activeFilter: ThreadFilterType
  searchQuery: string
  channels: CommunicationChannel[]
  onFilterChange: (filter: ThreadFilterType) => void
  onSearchChange: (query: string) => void
  /** Side effects on thread navigation (e.g. close mobile sidebar). Navigation uses Link href. */
  onThreadSelect: (threadId: string) => void
  /** Side effects on new-thread navigation. Navigation uses Link href. */
  onNewThread: () => void
  isLoading?: boolean
  /** Returns the href for a thread - enables right-click open in new tab */
  getThreadHref?: (threadId: string) => string
  /** Returns the href for creating a new thread */
  getNewThreadHref?: () => string
  /** Custom Link component (e.g., Next.js Link) for client-side navigation */
  LinkComponent?: React.ComponentType<LinkComponentProps>
  /** Callback when a thread is renamed */
  onThreadRename?: (threadId: string, newTitle: string) => void
  // Stable list order props
  /** Number of new threads since last load */
  pendingNewThreadCount?: number
  /** Timestamp when the list was loaded */
  listLoadedAt?: string | null
  /** Callback to accept and show new threads */
  onAcceptNewThreads?: () => void
  /** Whether there are more threads to load */
  hasMore?: boolean
  /** Whether we're currently loading more threads */
  loadingMore?: boolean
  /** Callback to load more threads */
  onLoadMore?: () => void
  /** Total distinct contacts with conversation threads (from server) */
  totalConversationContactCount?: number | null
  /** Toggle workplace-wide pin for a conversation thread */
  onToggleThreadPin?: (threadId: string, pinned: boolean) => void
  /** Sort contact groups in All Conversations */
  contactSort?: ContactSortMode
  onContactSortChange?: (sort: ContactSortMode) => void
}

const filterTabs: { id: ThreadFilterType; label: string }[] = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'conversations', label: 'Conversations' },
]

function getRelativeTime(dateString: string | null): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const contactSortOptions: {
  value: ContactSortMode
  triggerLabel: string
  menuLabel: string
}[] = [
  {
    value: 'last_activity',
    triggerLabel: 'Recent Activity',
    menuLabel: 'Sort By Recent Activity',
  },
  {
    value: 'last_contact',
    triggerLabel: 'Recent Messages',
    menuLabel: 'Sort By Recent Messages',
  },
]

function ContactSortDropdown({
  value,
  count,
  onChange,
}: {
  value: ContactSortMode
  count?: number
  onChange: (sort: ContactSortMode) => void
}) {
  const selectedLabel =
    contactSortOptions.find((option) => option.value === value)?.triggerLabel ??
    'Recent Activity'

  return (
    <div className="ml-auto flex shrink-0 items-center -mr-1.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Sort conversations"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex h-5 shrink-0 items-center gap-2 rounded-md border-0 bg-accent/80 pl-1.5 pr-1.5 text-[10px] font-medium text-muted-foreground/80 cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <span className="whitespace-nowrap">{selectedLabel}</span>
            {count !== undefined && count > 0 && (
              <span
                className={cn(
                  sidebarMetaColumnClass,
                  'text-muted-foreground/70',
                )}
              >
                {count}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-[11rem] p-1"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuRadioGroup
            value={value}
            onValueChange={(next) => onChange(next as ContactSortMode)}
          >
            {contactSortOptions.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value}
                className="text-xs"
              >
                {option.menuLabel}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function getFutureRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()

  if (diffMs <= 0) return 'now'

  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `in ${diffMins}m`
  if (diffHours < 24) return `in ${diffHours}h`
  if (diffDays < 7) return `in ${diffDays}d`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function isScheduledStatusActive(thread: ThreadItem): boolean {
  return (
    !!thread.hasScheduledMessage &&
    !!thread.nextScheduledAt &&
    !thread.hasActiveWorkflow &&
    !thread.hasActiveAgent &&
    (thread.pendingApprovalCount ?? 0) === 0
  )
}

function getThreadTimeLabel(thread: ThreadItem): string {
  if (isScheduledStatusActive(thread)) {
    return getFutureRelativeTime(thread.nextScheduledAt!)
  }
  return getRelativeTime(thread.lastMessageAt)
}

function getDayGroup(dateString: string | null): string {
  if (!dateString) return 'Older'
  const date = new Date(dateString)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const weekAgo = new Date(today.getTime() - 7 * 86400000)

  if (date >= today) return 'Today'
  if (date >= yesterday) return 'Yesterday'
  if (date >= weekAgo) return 'Last 7 Days'
  return 'Older'
}

type ConversationGroups = {
  pendingApproval: ContactGroup[]
  scheduled: ContactGroup[]
  all: ContactGroup[]
}

function isPendingApprovalStatus(thread: ThreadItem): boolean {
  return (thread.pendingApprovalCount ?? 0) > 0
}

function sortScheduledContactGroups(groups: ContactGroup[]): ContactGroup[] {
  return [...groups].sort((a, b) => {
    const aTime = a.nextScheduledAt
      ? new Date(a.nextScheduledAt).getTime()
      : Infinity
    const bTime = b.nextScheduledAt
      ? new Date(b.nextScheduledAt).getTime()
      : Infinity
    return aTime - bTime
  })
}

function groupConversationsByStatus(
  contactGroups: ContactGroup[],
): ConversationGroups {
  const pendingApproval: ContactGroup[] = []
  const scheduled: ContactGroup[] = []

  for (const group of contactGroups) {
    // Check if any thread in the group has pending approval
    const hasPendingApproval = group.threads.some(isPendingApprovalStatus)
    if (hasPendingApproval) {
      pendingApproval.push(group)
    }

    // Check if any thread in the group has scheduled message (and is the active scheduled status)
    const hasScheduled = group.threads.some(isScheduledStatusActive)
    if (hasScheduled && !hasPendingApproval) {
      scheduled.push(group)
    }
  }

  return {
    pendingApproval,
    scheduled: sortScheduledContactGroups(scheduled),
    all: contactGroups,
  }
}

function isThreadPinned(thread: ThreadItem): boolean {
  return !!thread.pinnedAt
}

function getGroupLatestPinnedAt(group: ContactGroup): number {
  return group.threads.reduce((latest, thread) => {
    if (!thread.pinnedAt) return latest
    const pinnedAt = new Date(thread.pinnedAt).getTime()
    return pinnedAt > latest ? pinnedAt : latest
  }, 0)
}

function sortPinnedContactGroups(groups: ContactGroup[]): ContactGroup[] {
  return [...groups].sort((a, b) => {
    const aPinned = getGroupLatestPinnedAt(a)
    const bPinned = getGroupLatestPinnedAt(b)
    if (bPinned !== aPinned) return bPinned - aPinned
    const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
    const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
    return bTime - aTime
  })
}

/** Shared right column for section counts and tile timestamps */
const sidebarMetaColumnClass = 'min-w-8 shrink-0 text-right tabular-nums'

type ThreadSidebarMetaProps = {
  timeLabel: string
  isPinned: boolean
  isSelected?: boolean
  showPinAction?: boolean
  onTogglePin?: (pinned: boolean) => void
}

function ThreadSidebarMeta({
  timeLabel,
  isPinned,
  isSelected = false,
  showPinAction = false,
  onTogglePin,
}: ThreadSidebarMetaProps) {
  const canPin = showPinAction && onTogglePin

  return (
    <span
      className={cn(
        sidebarMetaColumnClass,
        'text-[10px] self-center',
        isSelected ? 'text-accent-foreground/60' : 'text-muted-foreground/60',
      )}
    >
      {canPin ? (
        <>
          <span className="group-hover:hidden">{timeLabel}</span>
          <button
            type="button"
            title={isPinned ? 'Unpin conversation' : 'Pin conversation'}
            className={cn(
              'hidden group-hover:inline-flex h-5 w-5 items-center justify-center rounded-md transition-colors',
              isSelected
                ? 'text-accent-foreground/70 hover:bg-accent-foreground/10'
                : 'text-muted-foreground/70 hover:bg-muted/60',
            )}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onTogglePin(!isPinned)
            }}
          >
            <Icon
              name={isPinned ? 'PinOff' : 'Pin'}
              className="h-3 w-3 shrink-0"
            />
          </button>
        </>
      ) : (
        timeLabel
      )}
    </span>
  )
}

type ConversationSectionKey = 'pendingApproval' | 'scheduled' | 'all'

type ContactGroupSectionKey = ConversationSectionKey | 'pinned'

type ConversationSectionExpandedState = Record<ConversationSectionKey, boolean>

const defaultConversationSectionExpanded: ConversationSectionExpandedState = {
  pendingApproval: true,
  scheduled: true,
  all: true,
}

const conversationSectionsStorageKey =
  'threads-sidebar-conversation-sections-expanded'

type InboxDayGroupKey = 'Today' | 'Yesterday' | 'Last 7 Days' | 'Older'

type InboxSectionExpandedState = Record<InboxDayGroupKey, boolean>

const defaultInboxSectionExpanded: InboxSectionExpandedState = {
  Today: true,
  Yesterday: true,
  'Last 7 Days': true,
  Older: true,
}

const inboxSectionsStorageKey = 'threads-sidebar-inbox-sections-expanded'

function getContactGroupKey(
  section: ContactGroupSectionKey,
  contactId: string,
): string {
  return `${section}:${contactId}`
}

function getThreadContactId(thread: ThreadItem): string | null {
  const contactParticipant = thread.participants.find(
    (p) => p.kind === 'CONTACT',
  )
  if (!contactParticipant) return null
  return contactParticipant.contactId || contactParticipant.id
}

function SectionGroupHeader({
  title,
  count,
  isExpanded = true,
  collapsible = false,
  sortControl,
}: {
  title: string
  count?: number
  isExpanded?: boolean
  collapsible?: boolean
  sortControl?: React.ReactNode
}) {
  const content = (
    <>
      <div className="flex w-2 shrink-0 items-center justify-center self-stretch">
        {collapsible && (
          <Icon
            name="ChevronDown"
            className={cn(
              'h-2.5 w-2.5 shrink-0 text-muted-foreground/50 transition-transform duration-200',
              !isExpanded && '-rotate-90',
            )}
          />
        )}
      </div>
      <span
        className={cn(
          'min-w-0 text-left text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wider',
          sortControl ? 'shrink-0' : 'flex-1',
        )}
      >
        {title}
      </span>
      {sortControl}
      {count !== undefined && count > 0 && !sortControl && (
        <span
          className={cn(
            sidebarMetaColumnClass,
            'flex items-center justify-end self-center',
          )}
        >
          <span className="-mr-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-accent px-1.5 text-[10px] font-medium leading-none text-muted-foreground/70 tabular-nums">
            {count}
          </span>
        </span>
      )}
    </>
  )

  const className =
    'flex w-full items-center gap-2 py-1 pl-3 pr-3 rounded-md transition-colors'

  if (collapsible) {
    return (
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(className, 'hover:bg-accent/30 focus:outline-none')}
        >
          {content}
        </button>
      </CollapsibleTrigger>
    )
  }

  return <div className={className}>{content}</div>
}

function generateInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getAvatarSeed(participant: ThreadParticipant): string | undefined {
  if (participant.kind === 'CONTACT') {
    return participant.imageSeed || undefined
  }
  return participant.id
}

function threadStatusProps(thread: ThreadItem) {
  return {
    hasActiveWorkflow: thread.hasActiveWorkflow,
    hasActiveAgent: thread.hasActiveAgent,
    pendingApprovalCount: thread.pendingApprovalCount,
    hasScheduledMessage: thread.hasScheduledMessage,
    size: 'sm' as const,
  }
}

function threadMatchesFilter(
  thread: ThreadItem,
  activeFilter: ThreadFilterType,
  currentMemberId: string | null,
): boolean {
  if (activeFilter === 'inbox') {
    return isThreadInInbox(thread, currentMemberId)
  }
  return isThreadInConversations(thread)
}

function isGroupScheduledStatusActive(group: ContactGroup): boolean {
  return (
    group.threads.some((t) => t.hasScheduledMessage) &&
    !!group.nextScheduledAt &&
    !group.threads.some((t) => t.hasActiveWorkflow || t.hasActiveAgent) &&
    group.threads.every((t) => (t.pendingApprovalCount ?? 0) === 0)
  )
}

function getGroupTimeLabel(
  group: ContactGroup,
  sortMode: ContactSortMode = 'last_activity',
): string {
  if (isGroupScheduledStatusActive(group)) {
    return getFutureRelativeTime(group.nextScheduledAt!)
  }
  const timeAt =
    sortMode === 'last_contact'
      ? group.lastContactedAt ?? group.displayTimeAt
      : group.lastActivityAt ?? group.displayTimeAt
  return getRelativeTime(timeAt ?? group.lastMessageAt)
}

function shouldHideThreadTileAvatar(thread: ThreadItem): boolean {
  if (thread.routingParticipantId) return false
  if (thread.participants.some((p) => p.kind === 'CONTACT')) return false

  const members = thread.participants.filter((p) => p.kind === 'MEMBER')
  if (members.length > 1) return false

  const automationPeers = thread.participants.filter(
    (p) => p.kind === 'AGENT' || p.kind === 'WORKFLOW',
  )
  const otherPeers = thread.participants.filter(
    (p) =>
      p.kind !== 'MEMBER' &&
      p.kind !== 'AGENT' &&
      p.kind !== 'WORKFLOW' &&
      p.kind !== 'SYSTEM',
  )

  if (otherPeers.length > 0) return false

  return members.length + automationPeers.length > 0
}

function getThreadTileDisplay(thread: ThreadItem): {
  displayName: string
  avatarUrl: string | null
  participant: ThreadParticipant | null
} {
  const contact = thread.participants.find((p) => p.kind === 'CONTACT')
  if (contact) {
    return {
      displayName: contact.displayName,
      avatarUrl: contact.avatarUrl,
      participant: contact,
    }
  }

  const agent = thread.participants.find((p) => p.kind === 'AGENT')
  if (agent) {
    return {
      displayName: thread.title || agent.displayName,
      avatarUrl: agent.avatarUrl,
      participant: agent,
    }
  }

  const member = thread.participants.find((p) => p.kind === 'MEMBER')
  if (member) {
    return {
      displayName: thread.title || member.displayName,
      avatarUrl: member.avatarUrl,
      participant: member,
    }
  }

  return {
    displayName: thread.title || 'New Thread',
    avatarUrl: null,
    participant: null,
  }
}

type ConversationTileProps = {
  thread: ThreadItem
  isSelected: boolean
  onClick: () => void
  href?: string
  LinkComponent?: React.ComponentType<LinkComponentProps>
  showPinAction?: boolean
  onToggleThreadPin?: (threadId: string, pinned: boolean) => void
  onRename?: (newTitle: string) => void
}

function ConversationTile({
  thread,
  isSelected,
  onClick,
  href,
  LinkComponent,
  showPinAction = false,
  onToggleThreadPin,
  onRename,
}: ConversationTileProps) {
  const [isHovered, setIsHovered] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [editValue, setEditValue] = React.useState(thread.title || '')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const { displayName, avatarUrl, participant } = getThreadTileDisplay(thread)
  const hideAvatar = shouldHideThreadTileAvatar(thread)

  React.useEffect(() => {
    if (!isEditing) {
      setEditValue(thread.title || displayName)
    }
  }, [thread.title, displayName, isEditing])

  React.useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const handleSaveRename = () => {
    const nextTitle = editValue.trim()
    setIsEditing(false)
    if (nextTitle && onRename && nextTitle !== (thread.title || displayName)) {
      onRename(nextTitle)
    }
  }

  const content = (
    <div
      className="flex gap-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ThreadStatusDotRail {...threadStatusProps(thread)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {!hideAvatar && (
            <Avatar
              className={cn(
                'h-4 w-4 rounded-full shrink-0',
                thread.unreadCount > 0 &&
                  !isSelected &&
                  'ring-1 ring-primary/50',
              )}
            >
              <AvatarImage
                src={avatarUrl || undefined}
                seed={participant ? getAvatarSeed(participant) : undefined}
              />
              <AvatarFallback className="text-[8px]">
                {generateInitials(displayName)}
              </AvatarFallback>
            </Avatar>
          )}
          {isEditing ? (
            <input
              ref={inputRef}
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
              onBlur={handleSaveRename}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSaveRename()
                } else if (event.key === 'Escape') {
                  setIsEditing(false)
                  setEditValue(thread.title || displayName)
                }
              }}
              onClick={(event) => event.stopPropagation()}
              className="text-xs bg-transparent outline-none p-0 m-0 border-none text-foreground/80 flex-1 min-w-0"
            />
          ) : (
            <span
              className={cn(
                'text-xs truncate flex-1',
                isSelected
                  ? 'font-medium text-accent-foreground'
                  : thread.unreadCount > 0
                    ? 'font-semibold text-foreground'
                    : 'text-foreground/80',
              )}
            >
              {displayName}
            </span>
          )}
          {isHovered && onRename && !isEditing && (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setIsEditing(true)
              }}
              className="p-0.5 rounded hover:bg-muted-foreground/10 transition-colors shrink-0"
            >
              <Icon
                name="Pencil"
                className="h-2.5 w-2.5 text-muted-foreground/60"
              />
            </button>
          )}
        </div>
        <div
          className={cn(
            'flex items-center gap-1.5 mt-0.5',
            !hideAvatar && 'pl-5.5',
          )}
        >
          <span
            className={cn(
              'text-[11px] truncate flex-1',
              isSelected
                ? 'text-accent-foreground/70'
                : thread.unreadCount > 0
                  ? 'text-foreground/70'
                  : 'text-muted-foreground',
            )}
          >
            {thread.lastMessagePreview || 'No messages yet'}
          </span>
          {thread.unreadCount > 0 && (
            <span className="text-[10px] min-w-4 h-4 px-1 rounded flex items-center justify-center bg-primary text-primary-foreground shrink-0">
              {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
            </span>
          )}
        </div>
      </div>
      <ThreadSidebarMeta
        timeLabel={getThreadTimeLabel(thread)}
        isPinned={isThreadPinned(thread)}
        isSelected={isSelected}
        showPinAction={showPinAction}
        onTogglePin={
          onToggleThreadPin
            ? (pinned) => onToggleThreadPin(thread.id, pinned)
            : undefined
        }
      />
    </div>
  )

  const className = cn(
    'w-full px-3 py-1.5 rounded-lg text-left transition-colors group block',
    'hover:bg-muted/70 focus:outline-none focus:bg-muted/70',
    isSelected && 'bg-accent hover:bg-accent/90',
  )

  if (isEditing) {
    return <div className={className}>{content}</div>
  }

  return (
    <ThreadNavLink
      href={href}
      LinkComponent={LinkComponent}
      onClick={onClick}
      className={className}
    >
      {content}
    </ThreadNavLink>
  )
}

function getChannelIcon(
  channelType: string | undefined,
): React.ComponentProps<typeof Icon>['name'] {
  switch (channelType?.toLowerCase()) {
    case 'whatsapp':
      return 'MessageCircle'
    case 'email':
      return 'Mail'
    case 'sms':
      return 'Smartphone'
    case 'messenger':
    case 'facebook':
      return 'Facebook'
    case 'instagram':
      return 'Instagram'
    case 'telegram':
      return 'Send'
    case 'slack':
      return 'Hash'
    case 'discord':
      return 'Headphones'
    case 'web':
      return 'Globe'
    case 'face_to_face':
      return 'Users'
    default:
      return 'MessageSquare'
  }
}

type ContactThreadRowProps = {
  thread: ThreadItem
  isSelected: boolean
  channels: CommunicationChannel[]
  onClick: () => void
  href?: string
  LinkComponent?: React.ComponentType<LinkComponentProps>
  isLast?: boolean
  /** Vertical rhythm inside an expanded contact group tile */
  inExpandedGroup?: boolean
  connectorClassName?: string
  showPinAction?: boolean
  onToggleThreadPin?: (threadId: string, pinned: boolean) => void
}

function ContactThreadRow({
  thread,
  isSelected,
  channels,
  onClick,
  href,
  LinkComponent,
  isLast = false,
  inExpandedGroup = false,
  connectorClassName,
  showPinAction = false,
  onToggleThreadPin,
}: ContactThreadRowProps) {
  const channel = channels.find((c) => c.id === thread.communicationChannelId)
  const channelName = channel?.name || thread.title || 'Message'
  const channelIcon = getChannelIcon(channel?.type)
  const appIconUrl = channel?.appIconUrl

  const content = (
    <div className="flex flex-1 min-w-0 gap-2">
      <ThreadStatusDotRail
        {...threadStatusProps(thread)}
        connectorAbove={inExpandedGroup}
        connectorBelow={inExpandedGroup && !isLast}
        connectorExtendBelow={
          inExpandedGroup && !isLast ? 'calc(0.375rem - 1px)' : undefined
        }
        connectorClassName={connectorClassName}
      />
      <div className="flex w-3.5 shrink-0 self-stretch items-center justify-center">
        {appIconUrl ? (
          <img
            src={appIconUrl}
            alt={channelName}
            className={cn(
              'h-3.5 w-3.5 rounded object-contain shrink-0 transition-[filter,opacity]',
              isSelected
                ? 'opacity-100'
                : 'grayscale opacity-60 group-hover:opacity-80',
            )}
          />
        ) : (
          <Icon
            name={channelIcon}
            className={cn(
              'h-3 w-3 shrink-0 transition-colors',
              isSelected
                ? 'text-accent-foreground'
                : 'text-muted-foreground group-hover:text-foreground',
            )}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-xs truncate flex-1 transition-colors',
              isSelected
                ? 'font-medium text-accent-foreground'
                : thread.unreadCount > 0
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground group-hover:text-foreground group-hover:underline group-hover:decoration-dotted',
            )}
          >
            {channelName}
          </span>
          {thread.unreadCount > 0 && (
            <span
              className={cn(
                'text-[10px] min-w-4 h-4 px-1 rounded flex items-center justify-center shrink-0',
                isSelected
                  ? 'bg-accent-foreground/20 text-accent-foreground'
                  : 'bg-primary text-primary-foreground',
              )}
            >
              {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
            </span>
          )}
        </div>
        <p
          className={cn(
            'text-[10px] truncate mt-0.5',
            isSelected
              ? 'text-accent-foreground/60'
              : 'text-muted-foreground/50',
          )}
        >
          {thread.lastMessagePreview || 'No messages yet'}
        </p>
      </div>
      <ThreadSidebarMeta
        timeLabel={getThreadTimeLabel(thread)}
        isPinned={isThreadPinned(thread)}
        isSelected={isSelected}
        showPinAction={showPinAction}
        onTogglePin={
          onToggleThreadPin
            ? (pinned) => onToggleThreadPin(thread.id, pinned)
            : undefined
        }
      />
    </div>
  )

  const className = cn(
    'w-full text-left transition-colors px-3 rounded-md group block',
    inExpandedGroup ? 'pt-1.5 pb-0' : 'py-1.5',
  )

  return (
    <ThreadNavLink
      href={href}
      LinkComponent={LinkComponent}
      onClick={onClick}
      className={className}
    >
      {content}
    </ThreadNavLink>
  )
}

type FeedTileProps = {
  thread: ThreadItem
  isSelected: boolean
  onClick: () => void
  href?: string
  LinkComponent?: React.ComponentType<LinkComponentProps>
  onRename?: (newTitle: string) => void
}

function FeedTile({
  thread,
  isSelected,
  onClick,
  href,
  LinkComponent,
  onRename,
}: FeedTileProps) {
  const [isHovered, setIsHovered] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [displayTitle, setDisplayTitle] = React.useState(thread.title || '')
  const [editValue, setEditValue] = React.useState(thread.title || '')
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!isEditing) {
      setDisplayTitle(thread.title || '')
      setEditValue(thread.title || '')
    }
  }, [thread.title, isEditing])

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEdit = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setEditValue(displayTitle)
    setIsEditing(true)
  }

  const handleSave = () => {
    const nextTitle = editValue.trim()
    if (nextTitle && nextTitle !== displayTitle) {
      setDisplayTitle(nextTitle)
      onRename?.(nextTitle)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditValue(displayTitle)
      setIsEditing(false)
    }
  }

  const routingLabel = getFeedRoutingLabel(thread)
  const feedTitle = displayTitle || 'Feed'
  const feedSubtitle =
    thread.lastMessagePreview || routingLabel || 'No messages yet'

  const content = (
    <div
      className="flex gap-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ThreadStatusDotRail {...threadStatusProps(thread)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 h-4">
          <Avatar className="h-4 w-4 rounded-full shrink-0">
            <AvatarFallback className="text-[8px]">
              <Icon name="Rss" className="h-2 w-2" />
            </AvatarFallback>
          </Avatar>
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="text-xs bg-transparent outline-none p-0 m-0 border-none text-foreground/80 flex-1 min-w-0"
            />
          ) : (
            <span
              className={cn(
                'text-xs truncate flex-1 min-w-0',
                thread.unreadCount > 0
                  ? 'font-semibold text-foreground'
                  : 'text-foreground/80',
              )}
            >
              {feedTitle}
            </span>
          )}
          {isHovered && onRename && !isEditing && (
            <button
              onClick={handleStartEdit}
              className="p-0.5 rounded hover:bg-muted-foreground/10 transition-colors shrink-0"
            >
              <Icon
                name="Pencil"
                className="h-2.5 w-2.5 text-muted-foreground/60"
              />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 pl-5.5">
          <span
            className={cn(
              'text-[11px] truncate flex-1',
              thread.unreadCount > 0
                ? 'text-foreground/70'
                : 'text-muted-foreground',
            )}
          >
            {feedSubtitle}
          </span>
          {thread.unreadCount > 0 && (
            <span className="text-[10px] min-w-4 h-4 px-1 rounded flex items-center justify-center bg-primary text-primary-foreground shrink-0">
              {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
            </span>
          )}
        </div>
      </div>
      <span className="text-[10px] shrink-0 self-center text-muted-foreground/60">
        {getThreadTimeLabel(thread)}
      </span>
    </div>
  )

  const className = cn(
    'w-full px-3 py-1.5 rounded-md text-left transition-colors group block',
    'hover:bg-muted/70 focus:outline-none focus:bg-muted/70',
    isSelected && 'bg-muted/50',
  )

  if (isEditing) {
    return <div className={className}>{content}</div>
  }

  return (
    <ThreadNavLink
      href={href}
      LinkComponent={LinkComponent}
      onClick={onClick}
      className={className}
    >
      {content}
    </ThreadNavLink>
  )
}

type ContactGroupTileProps = {
  group: ContactGroup
  contactSort?: ContactSortMode
  selectedThreadId: string | null
  channels: CommunicationChannel[]
  onThreadSelect: (threadId: string) => void
  getThreadHref?: (threadId: string) => string
  LinkComponent?: React.ComponentType<LinkComponentProps>
  isExpanded: boolean
  onExpandChange: (expanded: boolean) => void
  showPinAction?: boolean
  onToggleThreadPin?: (threadId: string, pinned: boolean) => void
}

function ContactGroupTile({
  group,
  contactSort = 'last_activity',
  selectedThreadId,
  channels,
  onThreadSelect,
  getThreadHref,
  LinkComponent,
  isExpanded,
  onExpandChange,
  showPinAction = false,
  onToggleThreadPin,
}: ContactGroupTileProps) {
  const singleThread = group.threads.length === 1 ? group.threads[0]! : null
  const singleThreadHref =
    singleThread && getThreadHref ? getThreadHref(singleThread.id) : undefined
  const useLinkTrigger = Boolean(
    singleThread && singleThreadHref && LinkComponent && !isExpanded,
  )
  const hasSelectedThread = group.threads.some((t) => t.id === selectedThreadId)

  const contactParticipant: ThreadParticipant = {
    id: group.contactId,
    displayName: group.displayName,
    avatarUrl: group.avatarUrl,
    kind: 'CONTACT',
    imageSeed: group.imageSeed,
  }

  const groupStatusProps = {
    hasActiveWorkflow: group.threads.some((t) => t.hasActiveWorkflow),
    hasActiveAgent: group.threads.some((t) => t.hasActiveAgent),
    pendingApprovalCount: group.threads.reduce(
      (sum, t) => sum + (t.pendingApprovalCount ?? 0),
      0,
    ),
    hasScheduledMessage: group.threads.some((t) => t.hasScheduledMessage),
    size: 'sm' as const,
  }

  const groupStatusLineClass = getThreadStatusDotLineClass(groupStatusProps)

  const contactHeaderContent = (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <Avatar
          className={cn(
            'h-4 w-4 rounded-full shrink-0',
            group.totalUnreadCount > 0 &&
              !hasSelectedThread &&
              'ring-1 ring-primary/50',
          )}
        >
          <AvatarImage
            src={group.avatarUrl || undefined}
            seed={getAvatarSeed(contactParticipant)}
          />
          <AvatarFallback className="text-[8px]">
            {generateInitials(group.displayName)}
          </AvatarFallback>
        </Avatar>
        <span
          className={cn(
            'text-xs truncate flex-1',
            group.totalUnreadCount > 0
              ? 'font-semibold text-foreground'
              : 'text-foreground/80',
          )}
        >
          {group.displayName}
        </span>
      </div>
      {!isExpanded && (
        <div className="flex items-center gap-1.5 mt-0.5 pl-5.5">
          <span
            className={cn(
              'text-[11px] truncate flex-1',
              group.totalUnreadCount > 0
                ? 'text-foreground/70'
                : 'text-muted-foreground',
            )}
          >
            {group.lastMessagePreview || 'No messages yet'}
          </span>
          {group.totalUnreadCount > 0 && (
            <span className="text-[10px] min-w-4 h-4 px-1 rounded flex items-center justify-center bg-primary text-primary-foreground shrink-0">
              {group.totalUnreadCount > 99 ? '99+' : group.totalUnreadCount}
            </span>
          )}
        </div>
      )}
    </div>
  )

  const triggerClassName = cn(
    'w-full px-3 text-left transition-colors block',
    'focus:outline-none',
    isExpanded ? 'pb-1.5 pt-0' : 'py-1.5',
  )

  const triggerContent = (
    <div className="flex gap-2">
      <ThreadStatusDotRail
        {...groupStatusProps}
        outline={isExpanded}
        connectorBelow={isExpanded && group.threads.length > 0}
        connectorExtendBelow={
          isExpanded && group.threads.length > 0
            ? 'calc(0.75rem - 1px)'
            : undefined
        }
        connectorClassName={groupStatusLineClass}
      />
      {contactHeaderContent}
      {!isExpanded && (
        <ThreadSidebarMeta
          timeLabel={getGroupTimeLabel(group, contactSort)}
          isPinned={singleThread ? isThreadPinned(singleThread) : false}
          isSelected={hasSelectedThread}
          showPinAction={showPinAction && !!singleThread}
          onTogglePin={
            singleThread && onToggleThreadPin
              ? (pinned) => onToggleThreadPin(singleThread.id, pinned)
              : undefined
          }
        />
      )}
    </div>
  )

  return (
    <Collapsible open={isExpanded} onOpenChange={onExpandChange}>
      <div
        className={cn(
          'rounded-md transition-colors group',
          isExpanded && 'py-1.5',
          isExpanded
            ? 'bg-accent hover:bg-accent/90'
            : hasSelectedThread
              ? 'bg-muted/50'
              : 'hover:bg-muted/70',
        )}
      >
        <CollapsibleTrigger asChild>
          {useLinkTrigger && singleThread ? (
            <ThreadNavLink
              href={singleThreadHref}
              LinkComponent={LinkComponent}
              onClick={() => onThreadSelect(singleThread.id)}
              className={triggerClassName}
            >
              {triggerContent}
            </ThreadNavLink>
          ) : (
            <button type="button" className={triggerClassName}>
              {triggerContent}
            </button>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          {group.threads.map((thread, index) => (
            <ContactThreadRow
              key={thread.id}
              thread={thread}
              isSelected={thread.id === selectedThreadId}
              channels={channels}
              onClick={() => onThreadSelect(thread.id)}
              href={getThreadHref?.(thread.id)}
              isLast={index === group.threads.length - 1}
              inExpandedGroup={isExpanded}
              connectorClassName={groupStatusLineClass}
              LinkComponent={LinkComponent}
              showPinAction={showPinAction}
              onToggleThreadPin={onToggleThreadPin}
            />
          ))}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

function LoadMoreSpinner() {
  return (
    <div className="flex items-center justify-center py-2">
      <div className="animate-spin rounded-full h-3 w-3 border border-muted-foreground border-t-transparent" />
    </div>
  )
}

type ThreadListInfiniteScrollProps = {
  scrollContainer: HTMLElement | null
  hasMore: boolean
  loadingMore: boolean
  onLoadMore?: () => void
  enabled: boolean
  className?: string
  children: React.ReactNode
}

function ThreadListInfiniteScroll({
  scrollContainer,
  hasMore,
  loadingMore,
  onLoadMore,
  enabled,
  className,
  children,
}: ThreadListInfiniteScrollProps) {
  const handleLoadMore = React.useCallback(() => {
    if (hasMore && !loadingMore) {
      onLoadMore?.()
    }
  }, [hasMore, loadingMore, onLoadMore])

  if (!enabled || !hasMore || !onLoadMore) {
    return (
      <div className={className}>
        {children}
        {loadingMore && <LoadMoreSpinner />}
      </div>
    )
  }

  return (
    <InfiniteScroll
      id="threads-sidebar-list"
      direction="down"
      scrollContainer={scrollContainer}
      handleLoadMoreItems={handleLoadMore}
      threshold={100}
      isLoading={loadingMore}
      className={className}
    >
      {children}
      {loadingMore && <LoadMoreSpinner />}
    </InfiniteScroll>
  )
}

export function ThreadsSidebar({
  threads,
  exclusiveFeedThreads = [],
  selectedThreadId,
  currentMemberId,
  activeFilter,
  searchQuery,
  channels,
  onFilterChange,
  onSearchChange,
  onThreadSelect,
  onNewThread,
  getThreadHref,
  getNewThreadHref,
  LinkComponent,
  onThreadRename,
  pendingNewThreadCount = 0,
  listLoadedAt,
  onAcceptNewThreads,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  totalConversationContactCount = null,
  onToggleThreadPin,
  contactSort: contactSortProp = 'last_activity',
  onContactSortChange,
}: ThreadsSidebarProps) {
  const contactSort = contactSortProp

  const handleContactSortChange = React.useCallback(
    (sort: ContactSortMode) => {
      onContactSortChange?.(sort)
    },
    [onContactSortChange],
  )

  const filteredThreads = React.useMemo(() => {
    return threads.filter(
      (thread) =>
        threadMatchesFilter(thread, activeFilter, currentMemberId) &&
        threadMatchesSearch(thread, searchQuery),
    )
  }, [threads, activeFilter, currentMemberId, searchQuery])

  const groupedThreads = React.useMemo(() => {
    if (activeFilter !== 'inbox') return null

    const groups: Record<string, ThreadItem[]> = {
      Today: [],
      Yesterday: [],
      'Last 7 Days': [],
      Older: [],
    }

    filteredThreads.forEach((thread) => {
      const group = getDayGroup(thread.lastMessageAt)
      const groupArray = groups[group]
      if (groupArray) {
        groupArray.push(thread)
      }
    })

    return groups
  }, [filteredThreads, activeFilter])

  const {
    contactGroups,
    feedThreads,
    conversationGroups,
    pinnedContactGroups,
  } = React.useMemo(() => {
    if (activeFilter !== 'conversations') {
      return {
        contactGroups: null,
        feedThreads: [],
        conversationGroups: null,
        pinnedContactGroups: [] as ContactGroup[],
      }
    }

    const hasSearch = searchQuery.trim().length > 0
    const pinnedFeedIds = new Set(
      exclusiveFeedThreads.map((thread) => thread.id),
    )

    if (!hasSearch && exclusiveFeedThreads.length > 0) {
      const feeds = exclusiveFeedThreads.filter((thread) =>
        threadMatchesFilter(thread, activeFilter, currentMemberId),
      )
      const regularThreads = filteredThreads.filter(
        (thread) => !pinnedFeedIds.has(thread.id),
      )
      const pinnedContactIds = new Set(
        regularThreads
          .filter(isThreadPinned)
          .map(getThreadContactId)
          .filter((id): id is string => id !== null),
      )
      const pinnedGroups = sortPinnedContactGroups(
        groupThreadsByContact(
          regularThreads.filter((thread) => {
            const contactId = getThreadContactId(thread)
            return contactId !== null && pinnedContactIds.has(contactId)
          }),
          contactSort,
        ),
      )
      const unpinnedRegularThreads = regularThreads.filter((thread) => {
        const contactId = getThreadContactId(thread)
        return contactId === null || !pinnedContactIds.has(contactId)
      })
      const groups = groupThreadsByContact(unpinnedRegularThreads, contactSort)
      return {
        contactGroups: groups,
        feedThreads: feeds,
        conversationGroups: groupConversationsByStatus(groups),
        pinnedContactGroups: pinnedGroups,
      }
    }

    // Search or no pinned feeds: don't pin feeds above pagination while searching
    const feeds = hasSearch
      ? []
      : filteredThreads.filter((t) => isFeedThread(t))
    const regularThreads = hasSearch
      ? filteredThreads
      : filteredThreads.filter((t) => !isFeedThread(t))
    const pinnedContactIds = new Set(
      regularThreads
        .filter(isThreadPinned)
        .map(getThreadContactId)
        .filter((id): id is string => id !== null),
    )
    const pinnedGroups = sortPinnedContactGroups(
      groupThreadsByContact(
        regularThreads.filter((thread) => {
          const contactId = getThreadContactId(thread)
          return contactId !== null && pinnedContactIds.has(contactId)
        }),
        contactSort,
      ),
    )
    const unpinnedRegularThreads = regularThreads.filter((thread) => {
      const contactId = getThreadContactId(thread)
      return contactId === null || !pinnedContactIds.has(contactId)
    })
    const groups = groupThreadsByContact(unpinnedRegularThreads, contactSort)
    return {
      contactGroups: groups,
      feedThreads: feeds,
      conversationGroups: groupConversationsByStatus(groups),
      pinnedContactGroups: pinnedGroups,
    }
  }, [
    filteredThreads,
    activeFilter,
    exclusiveFeedThreads,
    searchQuery,
    currentMemberId,
    contactSort,
  ])

  const [sectionExpanded, setSectionExpanded] =
    useSessionStorage<ConversationSectionExpandedState>(
      conversationSectionsStorageKey,
      defaultConversationSectionExpanded,
    )

  const conversationSectionExpanded = React.useMemo(
    (): ConversationSectionExpandedState => ({
      ...defaultConversationSectionExpanded,
      ...sectionExpanded,
    }),
    [sectionExpanded],
  )

  const setConversationSectionExpanded = React.useCallback(
    (key: ConversationSectionKey, open: boolean) => {
      setSectionExpanded((prev) => ({
        ...defaultConversationSectionExpanded,
        ...prev,
        [key]: open,
      }))
    },
    [setSectionExpanded],
  )

  const [inboxSectionExpandedState, setInboxSectionExpandedState] =
    useSessionStorage<InboxSectionExpandedState>(
      inboxSectionsStorageKey,
      defaultInboxSectionExpanded,
    )

  const inboxSectionExpanded = React.useMemo(
    (): InboxSectionExpandedState => ({
      ...defaultInboxSectionExpanded,
      ...inboxSectionExpandedState,
    }),
    [inboxSectionExpandedState],
  )

  const setInboxSectionExpanded = React.useCallback(
    (key: InboxDayGroupKey, open: boolean) => {
      setInboxSectionExpandedState((prev) => ({
        ...defaultInboxSectionExpanded,
        ...prev,
        [key]: open,
      }))
    },
    [setInboxSectionExpandedState],
  )

  // Track which contact group is expanded (accordion - only one at a time)
  const [expandedGroupKey, setExpandedGroupKey] = React.useState<string | null>(
    null,
  )

  // Auto-expand the group containing the selected thread on initial load
  React.useEffect(() => {
    if (!selectedThreadId || expandedGroupKey !== null) return

    const sections: Array<{
      key: ContactGroupSectionKey
      groups: ContactGroup[] | null | undefined
    }> = [
      { key: 'pinned', groups: pinnedContactGroups },
      { key: 'pendingApproval', groups: conversationGroups?.pendingApproval },
      { key: 'scheduled', groups: conversationGroups?.scheduled },
      { key: 'all', groups: contactGroups },
    ]

    for (const section of sections) {
      const groupWithSelected = section.groups?.find((g) =>
        g.threads.some((t) => t.id === selectedThreadId),
      )
      if (groupWithSelected) {
        setExpandedGroupKey(
          getContactGroupKey(section.key, groupWithSelected.contactId),
        )
        break
      }
    }
  }, [
    selectedThreadId,
    contactGroups,
    conversationGroups,
    pinnedContactGroups,
    expandedGroupKey,
  ])

  const handleContactGroupToggle = (groupKey: string, expanded: boolean) => {
    if (expanded) {
      setExpandedGroupKey(groupKey)
    } else {
      setExpandedGroupKey(null)
    }
  }

  // Check if any visible thread has active scheduled status
  const hasScheduledThreads = React.useMemo(() => {
    return filteredThreads.some(isScheduledStatusActive)
  }, [filteredThreads])

  // Trigger re-render every 60s when scheduled timestamps are visible
  const [, setTick] = React.useState(0)
  React.useEffect(() => {
    if (!hasScheduledThreads) return
    const interval = setInterval(() => setTick((t) => t + 1), 60000)
    return () => clearInterval(interval)
  }, [hasScheduledThreads])

  const conversationPinProps = {
    showPinAction: !!onToggleThreadPin,
    onToggleThreadPin,
  }

  const [scrollContainer, setScrollContainer] =
    React.useState<HTMLElement | null>(null)

  const handleListScrollRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setScrollContainer(node)
    },
    [],
  )

  const infiniteScrollEnabled =
    activeFilter === 'inbox' || activeFilter === 'conversations'

  const allConversationsCount =
    totalConversationContactCount ?? contactGroups?.length ?? 0

  const renderThread = (thread: ThreadItem) => {
    const isSelected = thread.id === selectedThreadId
    const href = getThreadHref?.(thread.id)

    if (isFeedThread(thread)) {
      return (
        <FeedTile
          key={thread.id}
          thread={thread}
          isSelected={isSelected}
          onClick={() => onThreadSelect(thread.id)}
          href={href}
          LinkComponent={LinkComponent}
          onRename={
            onThreadRename
              ? (newTitle) => onThreadRename(thread.id, newTitle)
              : undefined
          }
        />
      )
    }

    return (
      <ConversationTile
        key={thread.id}
        thread={thread}
        isSelected={isSelected}
        onClick={() => onThreadSelect(thread.id)}
        href={href}
        LinkComponent={LinkComponent}
        showPinAction={activeFilter === 'conversations' && !!onToggleThreadPin}
        onToggleThreadPin={onToggleThreadPin}
        onRename={
          thread.kind === 'BROADCAST' && onThreadRename
            ? (newTitle) => onThreadRename(thread.id, newTitle)
            : undefined
        }
      />
    )
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Title row — h-16 + px-4 matches TopNavigation action bar */}
      <div className="flex h-16 shrink-0 items-center justify-between px-4">
        <h2 className="text-sm font-semibold text-foreground">Threads</h2>
        <ThreadNavLink
          href={getNewThreadHref?.()}
          LinkComponent={LinkComponent}
          onClick={onNewThread}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent transition-colors"
        >
          <Icon name="Plus" className="h-4 w-4 text-muted-foreground" />
        </ThreadNavLink>
      </div>

      {/* Filter Tabs */}
      <div className="px-4 pb-3">
        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onFilterChange(tab.id)}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                activeFilter === tab.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pb-2">
        <div className="flex items-center gap-2 h-8 px-2.5 rounded-md text-muted-foreground/70 bg-accent/30 hover:bg-accent/40 focus-within:bg-accent/30 transition-colors">
          <Icon name="Search" className="h-3.5 w-3.5 shrink-0 opacity-50" />
          <input
            id="threads-sidebar-search"
            type="text"
            placeholder="Search threads..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="flex-1 min-w-0 bg-transparent border-0 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-0"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="shrink-0 rounded p-0.5 hover:bg-accent/80 transition-colors"
              title="Clear search"
            >
              <Icon name="X" className="h-3 w-3 opacity-50" />
            </button>
          )}
        </div>
      </div>

      {/* Thread List */}
      <div
        ref={handleListScrollRef}
        className="flex-1 overflow-y-auto px-2 pb-2"
      >
        {filteredThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <Icon
              name="MessagesSquare"
              className="h-8 w-8 text-muted-foreground/40 mb-2"
            />
            <p className="text-sm text-muted-foreground">No threads found</p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              {searchQuery
                ? 'Try adjusting your search'
                : 'Start a new thread to get started'}
            </p>
          </div>
        ) : activeFilter === 'inbox' && groupedThreads ? (
          <ThreadListInfiniteScroll
            scrollContainer={scrollContainer}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
            enabled={infiniteScrollEnabled}
            className="space-y-2"
          >
            {/* New threads alert */}
            {pendingNewThreadCount > 0 && onAcceptNewThreads && (
              <button
                onClick={onAcceptNewThreads}
                className="w-full px-3 py-2 text-xs text-primary bg-primary/5 hover:bg-primary/10 rounded-md flex items-center gap-2 transition-colors"
              >
                <Icon name="RefreshCw" className="h-3 w-3" />
                <span>
                  {pendingNewThreadCount} new thread
                  {pendingNewThreadCount > 1 ? 's' : ''} since{' '}
                  {listLoadedAt ? getRelativeTime(listLoadedAt) : 'earlier'}
                </span>
              </button>
            )}

            {(
              Object.entries(groupedThreads) as Array<
                [InboxDayGroupKey, ThreadItem[]]
              >
            ).map(
              ([group, threads]) =>
                threads.length > 0 && (
                  <Collapsible
                    key={group}
                    open={inboxSectionExpanded[group]}
                    onOpenChange={(open) =>
                      setInboxSectionExpanded(group, open)
                    }
                  >
                    <SectionGroupHeader
                      title={group}
                      count={threads.length}
                      isExpanded={inboxSectionExpanded[group]}
                      collapsible
                    />
                    <CollapsibleContent>
                      <div className="space-y-0.5">
                        {threads.map(renderThread)}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ),
            )}
          </ThreadListInfiniteScroll>
        ) : activeFilter === 'conversations' &&
          (contactGroups ||
            feedThreads.length > 0 ||
            pinnedContactGroups.length > 0) ? (
          <ThreadListInfiniteScroll
            scrollContainer={scrollContainer}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
            enabled={infiniteScrollEnabled}
            className="space-y-2"
          >
            {/* New threads alert */}
            {pendingNewThreadCount > 0 && onAcceptNewThreads && (
              <button
                onClick={onAcceptNewThreads}
                className="w-full px-3 py-2 text-xs text-primary bg-primary/5 hover:bg-primary/10 rounded-md flex items-center gap-2 transition-colors"
              >
                <Icon name="RefreshCw" className="h-3 w-3" />
                <span>
                  {pendingNewThreadCount} new conversation
                  {pendingNewThreadCount > 1 ? 's' : ''} since{' '}
                  {listLoadedAt ? getRelativeTime(listLoadedAt) : 'earlier'}
                </span>
              </button>
            )}

            {/* Pinned conversations + feeds at top */}
            {(pinnedContactGroups.length > 0 || feedThreads.length > 0) && (
              <div className="space-y-0.5">
                {pinnedContactGroups.map((group) => (
                  <ContactGroupTile
                    key={`pinned-${group.contactId}`}
                    group={group}
                    contactSort={contactSort}
                    selectedThreadId={selectedThreadId}
                    channels={channels}
                    onThreadSelect={onThreadSelect}
                    getThreadHref={getThreadHref}
                    LinkComponent={LinkComponent}
                    isExpanded={
                      expandedGroupKey ===
                      getContactGroupKey('pinned', group.contactId)
                    }
                    onExpandChange={(expanded) =>
                      handleContactGroupToggle(
                        getContactGroupKey('pinned', group.contactId),
                        expanded,
                      )
                    }
                    {...conversationPinProps}
                  />
                ))}
                {feedThreads.map((thread) => (
                  <FeedTile
                    key={thread.id}
                    thread={thread}
                    isSelected={thread.id === selectedThreadId}
                    onClick={() => onThreadSelect(thread.id)}
                    href={getThreadHref?.(thread.id)}
                    LinkComponent={LinkComponent}
                    onRename={
                      onThreadRename
                        ? (newTitle) => onThreadRename(thread.id, newTitle)
                        : undefined
                    }
                  />
                ))}
              </div>
            )}

            {/* Pending Approval section */}
            {conversationGroups?.pendingApproval &&
              conversationGroups.pendingApproval.length > 0 && (
                <Collapsible
                  open={conversationSectionExpanded.pendingApproval}
                  onOpenChange={(open) =>
                    setConversationSectionExpanded('pendingApproval', open)
                  }
                >
                  <SectionGroupHeader
                    title="Pending Approval"
                    count={conversationGroups.pendingApproval.length}
                    isExpanded={conversationSectionExpanded.pendingApproval}
                    collapsible
                  />
                  <CollapsibleContent>
                    <div className="space-y-0.5">
                      {conversationGroups.pendingApproval.map((group) => (
                        <ContactGroupTile
                          key={`pending-${group.contactId}`}
                          group={group}
                          contactSort={contactSort}
                          selectedThreadId={selectedThreadId}
                          channels={channels}
                          onThreadSelect={onThreadSelect}
                          getThreadHref={getThreadHref}
                          LinkComponent={LinkComponent}
                          isExpanded={
                            expandedGroupKey ===
                            getContactGroupKey(
                              'pendingApproval',
                              group.contactId,
                            )
                          }
                          onExpandChange={(expanded) =>
                            handleContactGroupToggle(
                              getContactGroupKey(
                                'pendingApproval',
                                group.contactId,
                              ),
                              expanded,
                            )
                          }
                          {...conversationPinProps}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

            {/* Scheduled section */}
            {conversationGroups?.scheduled &&
              conversationGroups.scheduled.length > 0 && (
                <Collapsible
                  open={conversationSectionExpanded.scheduled}
                  onOpenChange={(open) =>
                    setConversationSectionExpanded('scheduled', open)
                  }
                >
                  <SectionGroupHeader
                    title="Scheduled"
                    count={conversationGroups.scheduled.length}
                    isExpanded={conversationSectionExpanded.scheduled}
                    collapsible
                  />
                  <CollapsibleContent>
                    <div className="space-y-0.5">
                      {conversationGroups.scheduled.map((group) => (
                        <ContactGroupTile
                          key={`scheduled-${group.contactId}`}
                          group={group}
                          contactSort={contactSort}
                          selectedThreadId={selectedThreadId}
                          channels={channels}
                          onThreadSelect={onThreadSelect}
                          getThreadHref={getThreadHref}
                          LinkComponent={LinkComponent}
                          isExpanded={
                            expandedGroupKey ===
                            getContactGroupKey('scheduled', group.contactId)
                          }
                          onExpandChange={(expanded) =>
                            handleContactGroupToggle(
                              getContactGroupKey('scheduled', group.contactId),
                              expanded,
                            )
                          }
                          {...conversationPinProps}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

            {/* All Conversations section */}
            {contactGroups && contactGroups.length > 0 && (
              <Collapsible
                open={conversationSectionExpanded.all}
                onOpenChange={(open) =>
                  setConversationSectionExpanded('all', open)
                }
              >
                <SectionGroupHeader
                  title="Contacts"
                  isExpanded={conversationSectionExpanded.all}
                  collapsible
                  sortControl={
                    <ContactSortDropdown
                      value={contactSort}
                      count={allConversationsCount}
                      onChange={handleContactSortChange}
                    />
                  }
                />
                <CollapsibleContent>
                  <div className="space-y-0.5">
                    {contactGroups.map((group) => (
                      <ContactGroupTile
                        key={group.contactId}
                        group={group}
                        contactSort={contactSort}
                        selectedThreadId={selectedThreadId}
                        channels={channels}
                        onThreadSelect={onThreadSelect}
                        getThreadHref={getThreadHref}
                        LinkComponent={LinkComponent}
                        isExpanded={
                          expandedGroupKey ===
                          getContactGroupKey('all', group.contactId)
                        }
                        onExpandChange={(expanded) =>
                          handleContactGroupToggle(
                            getContactGroupKey('all', group.contactId),
                            expanded,
                          )
                        }
                        {...conversationPinProps}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </ThreadListInfiniteScroll>
        ) : (
          <div className="space-y-0.5">{filteredThreads.map(renderThread)}</div>
        )}
      </div>
    </div>
  )
}

export default ThreadsSidebar
